import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { UseFormSetValue } from "react-hook-form";
import { queryCommonFlags } from "@/api/constants";
import { getLeadsJourneysUserHierarchy } from "@/modules/tasks/api";
import { getLinkToOptionsData } from "./options";
import {
  LeadOption,
  AddTaskFormFields,
  LinkToOptionsType,
  LinkedEntityParams,
} from "@/modules/tasks/types";
import { useMemo } from "react";
import { LinkToOptions } from "@/modules/tasks/enums";

export function useFetchLeads(
  open: boolean,
  setValueAddTask: UseFormSetValue<AddTaskFormFields>,
  setLinkToEntity: (value: string) => void,
  createLinkedEntity: (params: LinkedEntityParams) => any,
  linkTo?: LinkToOptionsType | null,
  userUId?: string
) {
  const [leadsSearchTerm, setLeadsSearchTerm] = useState<string>("");

  const { data: leadsData, isLoading: leadsLoading } = useQuery({
    queryKey: ["task-leads-search", leadsSearchTerm, userUId],
    queryFn: () => getLeadsJourneysUserHierarchy(leadsSearchTerm, userUId),
    ...queryCommonFlags,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: open && linkTo === LinkToOptions.LEAD,
  });

  const handleLeadsSearch = useCallback((searchTerm: string) => {
    setLeadsSearchTerm(searchTerm);
  }, []);

  const leadOptions = useMemo(() => {
    const rawLeadData = getLinkToOptionsData(leadsData, leadsSearchTerm);
    return rawLeadData.map((option) => ({
      uid: option.uid,
      value: option.value,
      label: option.label,
    }));
  }, [leadsData, leadsSearchTerm]);

  // ===== LEAD SELECTION HANDLER =====

  const handleLeadSelection = useCallback(
    (
      value: string,
      linkTo: LinkToOptionsType,
      linkToOptionsData: LeadOption[],
      fieldOnChange: (value: any[]) => void
    ) => {
      setLinkToEntity(value || "");

      if (!value) {
        fieldOnChange([]);
        return;
      }

      const selectedOption = linkToOptionsData.find(
        (option) => option.value === value
      );
      if (!selectedOption) return;

      const linkedEntity = createLinkedEntity({
        name: selectedOption.label,
        systemUId: selectedOption.uid,
        id: selectedOption.value,
        type: linkTo,
      });

      setValueAddTask("linkedEntities", [linkedEntity]);
      fieldOnChange([linkedEntity]);
    },
    [setValueAddTask, setLinkToEntity, createLinkedEntity]
  );

  const handleLeadChange = useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement>,
      linkTo: LinkToOptionsType,
      options: LeadOption[],
      fieldOnChange: (value: any[]) => void
    ) => {
      handleLeadSelection(event.target.value, linkTo, options, fieldOnChange);
    },
    [handleLeadSelection]
  );

  return {
    leadsSearchTerm,
    setLeadsSearchTerm,
    leadsData,
    leadOptions,
    handleLeadsSearch,
    handleLeadChange,
    isLoading: leadsLoading,
  };
}
